/**
 * Shared messages used across multiple components
 *
 * Message keys follow the format: formforge.shared.{section}.{element}
 *
 * IMPORTANT: All variables must use ICU Message Format syntax
 *
 * These messages are reused across multiple components - always use these keys
 * instead of creating duplicate entries in component-specific files.
 */
export const sharedMessages = {
  // Actions (used across multiple components)
  Submit: 'formforge.shared.actions.submit',
  'Submitting...': 'formforge.shared.actions.submitting',
  Clear: 'formforge.shared.actions.clear',
  'Close panel': 'formforge.shared.actions.close_panel',
  Close: 'formforge.shared.actions.close',
  'View Requests': 'formforge.shared.actions.view_requests',
  Exit: 'formforge.shared.actions.exit',
  'Refresh Page': 'formforge.shared.actions.refresh',
  'Reload Page': 'formforge.shared.actions.reload',
  'Go to Form Management': 'formforge.shared.actions.go_to_form_management',
  Save: 'formforge.shared.actions.save',
  Publish: 'formforge.shared.actions.publish',
  Preview: 'formforge.shared.actions.preview',
  Cancel: 'formforge.shared.actions.cancel',
  Delete: 'formforge.shared.actions.delete',
  Yes: 'formforge.shared.actions.yes',
  No: 'formforge.shared.actions.no',
  'Back to Forms': 'formforge.shared.actions.back_to_forms',
  Continue: 'formforge.shared.actions.continue',
  Import: 'formforge.shared.actions.import',
  Export: 'formforge.shared.actions.export',
  Share: 'formforge.shared.actions.share',

  // Common Labels
  'Loading...': 'formforge.shared.labels.loading',
  Select: 'formforge.shared.labels.select',
  Name: 'formforge.shared.labels.name',
  Description: 'formforge.shared.labels.description',
  URL: 'formforge.shared.labels.url',
  'Form ID': 'formforge.shared.labels.form_id',
  Tools: 'formforge.shared.labels.tools',
  'Panel settings': 'formforge.shared.labels.panel_settings',
  'Form Name': 'formforge.shared.labels.form_name',
  'Enter form name': 'formforge.shared.labels.enter_form_name',
  'Search SAV Roles...': 'formforge.shared.labels.search_sav_roles',
  Submitting: 'formforge.shared.actions.submitting_loader',
  'Select Category': 'formforge.shared.labels.select_category',

  // Status Messages
  'Submitted:': 'formforge.shared.status.submitted',
  'Request ID:': 'formforge.shared.status.request_id',
  'Orchestration Execution ID:': 'formforge.shared.status.execution_id',

  // Boolean Values
  True: 'formforge.shared.values.true',
  False: 'formforge.shared.values.false',
  '-': 'formforge.shared.values.empty',
  '•': 'formforge.shared.values.bullet',
  'Attribute Mappings ({mapped, number}/{total, number})':
    'formforge.shared.labels.attribute_mappings',
  'Your form is empty. Drag and drop fields to get started.':
    'formforge.shared.labels.empty_form',

  // Loading Messages
  'Loading more data...': 'formforge.shared.loading.loading_more',
  'Loading options...': 'formforge.shared.loading.loading_more_options',
  'Loading editor, please wait...': 'formforge.shared.loading.loading_editor',
  'Loading content, please wait...': 'formforge.shared.loading.loading_content',
  'You have exceeded the maximum character limit':
    'formforge.shared.validation.max_char_exceeded',

  // Error Messages (used across multiple components)
  'Unknown error occurred': 'formforge.shared.errors.unknown',
  'No response data or metadataRef not available for form prefilling':
    'formforge.shared.errors.no_response_data',
  'Form Management': 'formforge.management.header.title',

  // Data Provider Messages
  'The design is subject to change in the final version.':
    'formforge.shared.data_providers.sql.preview_notice',
  'Please select required fields first':
    'formforge.shared.data_providers.select_required_fields_first',
  'This field is required': 'formforge.shared.validation.field_required',
  'Page size must be between 5 and 5000':
    'formforge.shared.data_providers.page_size_range_error',

  // Cloudflare Turnstile Messages
  'Cloudflare Turnstile': 'formforge.shared.turnstile.aria_label',

  // Orchestration Toggle
  'Link with Orchestration':
    'formforge.shared.labels.link_with_orchestration',

  // Builder Validation - Form Name
  'Form name is required': 'formforge.builder.validation.form_name.required',
  'Form Name cannot exceed 100 characters':
    'formforge.builder.validation.form_name.max_length',
  'Only letters, numbers, spaces, hyphens, and underscores are allowed':
    'formforge.builder.validation.form_name.invalid_characters',
  'Form name must start with a letter or number':
    'formforge.builder.validation.form_name.invalid_start',
  'HTML tags are not allowed in form name':
    'formforge.builder.validation.form_name.no_html',

  // Builder Validation - Field Label
  'Label is required': 'formforge.builder.validation.label.required',
  'Label cannot exceed 255 characters':
    'formforge.builder.validation.label.max_length',
  'Label must start with a letter or number':
    'formforge.builder.validation.label.invalid_start',
  'Label contains unsupported characters':
    'formforge.builder.validation.label.invalid_characters',
  'HTML tags are not allowed in label':
    'formforge.builder.validation.label.no_html',
  'Angle brackets (< >) are not allowed in Field Label':
    'formforge.builder.validation.label.no_brackets',

  // Builder Validation - Element ID
  'Element ID is required':
    'formforge.builder.validation.element_id.required',
  'Element ID cannot exceed 50 characters':
    'formforge.builder.validation.element_id.max_length',
  'Element ID must start with a lowercase letter or number':
    'formforge.builder.validation.element_id.invalid_start',
  'Only lowercase letters, numbers, and underscores are allowed (no spaces).':
    'formforge.builder.validation.element_id.invalid_characters',
  'Element ID must be unique within the form':
    'formforge.builder.validation.element_id.not_unique',
  'HTML tags are not allowed in Element ID':
    'formforge.builder.validation.element_id.no_html',
  'This Element ID is reserved and cannot be used':
    'formforge.builder.validation.element_id.reserved_word',

  // Builder Validation - Placeholder
  'Placeholder cannot exceed 40 characters':
    'formforge.builder.validation.placeholder.max_length',
  'HTML tags are not allowed in Placeholder':
    'formforge.builder.validation.placeholder.no_html',

  // Builder Validation - Helper Text
  'Helptext cannot exceed 100 characters':
    'formforge.builder.validation.helptext.max_length',
  'HTML tags are not allowed in Helper Text':
    'formforge.builder.validation.helptext.no_html',
  'Angle brackets (< >) are not allowed in Helper Text':
    'formforge.builder.validation.helptext.no_brackets',

  // Builder Validation - Error Message
  'Error Message cannot exceed 255 characters':
    'formforge.builder.validation.error_message.max_length',
  'HTML tags are not allowed in Error Message':
    'formforge.builder.validation.error_message.no_html',
  'Angle brackets (< >) are not allowed in Error Message':
    'formforge.builder.validation.error_message.no_brackets',

  // Builder Validation - Fixed Group Options
  'Option label is required':
    'formforge.builder.validation.option_label.required',
  'Option label cannot exceed 200 characters':
    'formforge.builder.validation.option_label.max_length',
  'HTML tags are not allowed in Option label':
    'formforge.builder.validation.option_label.no_html',
  'Angle brackets (< >) are not allowed in Option label':
    'formforge.builder.validation.option_label.no_brackets',
  'Option value is required':
    'formforge.builder.validation.option_value.required',
  'Option value cannot exceed 200 characters':
    'formforge.builder.validation.option_value.max_length',

  // Builder Validation - Tooltip
  'Tooltip cannot exceed 80 characters':
    'formforge.builder.validation.tooltip.max_length',
  'HTML tags are not allowed in Tooltip':
    'formforge.builder.validation.tooltip.no_html',
  'Angle brackets (< >) are not allowed in Tooltip':
    'formforge.builder.validation.tooltip.no_brackets',

  // Builder Validation - Description
  'Form Description cannot exceed 300 characters':
    'formforge.builder.validation.description.max_length',
  'Only letters, numbers, spaces, and basic punctuation (hyphens, underscores, periods, commas, colons, parentheses) are allowed':
    'formforge.builder.validation.description.invalid_characters',
  'HTML tags are not allowed in description':
    'formforge.builder.validation.description.no_html',

  // Builder Validation - URL Slug
  'URL slug is required': 'formforge.builder.validation.url_slug.required',
  'URL cannot exceed 100 characters':
    'formforge.builder.validation.url_slug.max_length',
  'Only lowercase letters, numbers, and hyphens are allowed':
    'formforge.builder.validation.url_slug.invalid_characters',
  'Spaces are not allowed in URL slug':
    'formforge.builder.validation.url_slug.spaces_not_allowed',
  'This URL slug is reserved and cannot be used':
    'formforge.builder.validation.url_slug.reserved_word',
  "URL slug cannot start with 'sav-da-'":
    'formforge.builder.validation.url_slug.reserved_prefix',
  'URL slug cannot start or end with hyphens':
    'formforge.builder.validation.url_slug.leading_trailing_hyphens',
  'URL slug must be unique across all forms':
    'formforge.builder.validation.url_slug.unique_required',

  // Builder Validation - Target Entity / Form Action
  'Form category is required':
    'formforge.builder.validation.target_entity.required',
  'Please select a valid form category':
    'formforge.builder.validation.target_entity.invalid_selection',
  'Form action is required':
    'formforge.builder.validation.form_action.required',
  'Please select a valid form action':
    'formforge.builder.validation.form_action.invalid_selection',
  'Selected action is not valid for the chosen category':
    'formforge.builder.validation.form_action.category_mismatch',

  // Builder Validation - Approval Workflow
  'Approval workflow is required':
    'formforge.builder.validation.approval_workflow.required',
  'Please select a valid approval workflow':
    'formforge.builder.validation.approval_workflow.invalid_selection',
  'Selected workflow is not active':
    'formforge.builder.validation.approval_workflow.inactive_workflow',

  // Builder Validation - Terms & Rich Text
  'Terms link is required':
    'formforge.builder.validation.terms_link.required',
  'Inline message is required':
    'formforge.builder.validation.tnc_message.required',
  'Message must contain text wrapped in {} to create a clickable link':
    'formforge.builder.validation.tnc_message.invalid_format',
  'Content is required': 'formforge.builder.validation.rich_text.required',
} as const;

// Export type for type safety
export type SharedMessageKey = keyof typeof sharedMessages;
